import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col, Dropdown } from 'react-bootstrap';
import { apiService } from '../services/api';
import './EditUserModal.css';

const EditUserModal = ({ show, onHide, user, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'user',
    assetId: '',
    department: '',
    bio: '',
    photo: '',
    isLockedOut: false
  });
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        role: user.role || 'user',
        assetId: user.assetId || '',
        department: user.department || '',
        bio: user.bio || '',
        photo: user.photo || '',
        isLockedOut: user.isLockedOut || false
      });
    }
  }, [user]);

  // Load companies when modal opens
  useEffect(() => {
    if (show) {
      loadCompanies();
      setCompanySearch('');
      setShowCompanyDropdown(false);
    }
  }, [show]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      // Use the same endpoint as Add User Modal to get ALL companies (not paginated)
      const response = await apiService.getCompaniesForRegistration();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies. Please try again.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCompanySelect = (companyId) => {
    setFormData(prev => ({
      ...prev,
      assetId: companyId
    }));
    setShowCompanyDropdown(false);
  };

  const filteredCompanies = companies.filter(company => {
    if (!companySearch.trim()) return true;
    const searchValue = companySearch.toLowerCase();
    const companyLabel = `${company.company || ''} ${company.branch || ''} ${company.email || ''}`.toLowerCase();
    return companyLabel.includes(searchValue);
  });

  const selectedCompanyLabel = (() => {
    const selected = companies.find(c => `${c.id}` === `${formData.assetId}`);
    if (!selected) return 'Select a company (optional)';
    return `${selected.company} ${selected.branch ? `- ${selected.branch}` : ''}`.trim();
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiService.updateUserProfile(user.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        assetId: formData.assetId || null, // Convert empty string to null
        department: formData.department,
        bio: formData.bio,
        photo: formData.photo
      });
      
      setSuccess('User updated successfully!');
      
      // Call the callback to refresh the users list
      if (onUserUpdated) {
        onUserUpdated();
      }
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'user',
      assetId: '',
      department: '',
      bio: '',
      photo: '',
      isLockedOut: false
    });
    setError('');
    setSuccess('');
    setCompanySearch('');
    setShowCompanyDropdown(false);
    onHide();
  };

  if (!user) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="edit-user-modal">
      <Modal.Header closeButton>
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  minLength={2}
                  maxLength={50}
                  placeholder="Enter first name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  minLength={2}
                  maxLength={50}
                  placeholder="Enter last name"
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Email Address *</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter email address"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
            >
              <option value="user">User</option>
              <option value="companyAdmin">Company Admin</option>
              <option value="superAdmin">Super Admin</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Company Assignment</Form.Label>
            <Dropdown
              className="w-100"
              show={showCompanyDropdown}
              onToggle={(isOpen) => setShowCompanyDropdown(isOpen)}
            >
              <Dropdown.Toggle
                id="company-assignment-dropdown"
                className="form-select text-start w-100"
                variant="light"
                style={{ backgroundColor: '#fff', borderColor: '#ced4da', color: '#212529', fontSize: '0.875rem', fontWeight: 400 }}
                disabled={loadingCompanies}
              >
                {selectedCompanyLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-0">
                <div className="p-2 border-bottom">
                  <Form.Control
                    autoFocus
                    type="text"
                    placeholder="Search companies"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    disabled={loadingCompanies}
                  />
                </div>
                <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  <Dropdown.Item
                    key="none"
                    onClick={() => handleCompanySelect('')}
                    style={{ fontSize: '0.875rem', fontWeight: 400, color: '#212529' }}
                  >
                    Select a company (optional)
                  </Dropdown.Item>
                  {filteredCompanies.map(company => (
                    <Dropdown.Item
                      key={company.id}
                      onClick={() => handleCompanySelect(company.id)}
                      style={{ fontSize: '0.875rem', fontWeight: 400, color: '#212529' }}
                    >
                      {company.company} {company.branch ? `- ${company.branch}` : ''}
                    </Dropdown.Item>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
            {loadingCompanies && (
              <Form.Text className="text-muted">
                <Spinner size="sm" className="me-1" />
                Loading companies...
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Department</Form.Label>
            <Form.Control
              type="text"
              name="department"
              value={formData.department}
              onChange={handleInputChange}
              placeholder="Enter department"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Bio</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              placeholder="Enter user biography"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Photo URL</Form.Label>
            <Form.Control
              type="url"
              name="photo"
              value={formData.photo}
              onChange={handleInputChange}
              placeholder="Enter photo URL"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="isLockedOut"
              label="Lock User Account"
              checked={formData.isLockedOut}
              onChange={handleInputChange}
            />
            <Form.Text className="text-muted">
              Locked users cannot log in to the system
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading || !formData.firstName || !formData.lastName || !formData.email}
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Updating...
            </>
          ) : (
            'Update User'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserModal; 